import { isDateBeforeToday } from './isDateBeforeToday';
import { Listing } from '@zoopla/listing-search-queries/graphql.generated';

export type FeedListingItem = {
    listing: Listing;
    presentationType: string;
};

export type ResultsItem = {
    data: FeedListingItem;
    type: string;
};

const availableFromLabel = 'Available from';

const getPriceDropProps = (priceDrop: Listing['priceDrop']) => {
    if (!priceDrop) {
        return null;
    }

    const { lastPriceChangeDate, percentageChangeLabel } = priceDrop;

    return percentageChangeLabel
        ? {
              percentageChangeLabel,
              lastPriceChangeDate: `Last reduced: ${lastPriceChangeDate}`
          }
        : null;
};

const getFlagLabel = (flag?: string | null) =>
    flag !== 'Under offer' ? flag ?? null : null;

const isUnderOffer = (flag?: string | null) => flag === 'Under offer';

const getIsImmediatelyAvailable = (availableFrom: Listing['availableFrom']) =>
    availableFrom && isDateBeforeToday(availableFrom);

const getAvailableFromText = (availableFrom?: string | null) => {
    return getIsImmediatelyAvailable(availableFrom)
        ? 'immediately'
        : availableFrom ?? null;
};

const getAvailableFromLabelText = (
    availableFrom: string | null,
    translatedAvailableFromLabel: string
) => {
    return getIsImmediatelyAvailable(availableFrom)
        ? 'Available'
        : translatedAvailableFromLabel;
};

export type FormattedListing<T> = T & {
    flag: string | null;
    underOffer: boolean;
    availableFrom: string | null;
    availableFromLabel: string;
    priceDrop: {
        percentageChangeLabel: string;
        lastPriceChangeDate: string;
    } | null;
    isFavourite: boolean;
};

export const formatListings = <T>(
    listings: Array<
        T & {
            flag?: string | null;
            availableFrom?: string | null;
            priceDrop?: {
                lastPriceChangeDate?: string | null;
                percentageChangeLabel?: string | null;
            } | null;
        }
    >
): FormattedListing<T>[] =>
    listings.map((listing) => {
        const { flag, availableFrom, priceDrop } = listing;

        return {
            ...listing,
            flag: getFlagLabel(flag),
            underOffer: isUnderOffer(flag),
            availableFrom: getAvailableFromText(availableFrom),
            availableFromLabel: getAvailableFromLabelText(
                availableFrom ?? null,
                availableFromLabel
            ),
            priceDrop: getPriceDropProps(priceDrop),
            isFavourite: false
        };
    });

export const formatListingsV2 = (results: Array<ResultsItem>) =>
    results.map((feedListingItem) => {
        const { listing, presentationType } = feedListingItem.data;
        const { flag, availableFrom, priceDrop } = listing;

        return {
            ...listing,
            flag: getFlagLabel(flag),
            underOffer: isUnderOffer(flag),
            availableFrom: getAvailableFromText(availableFrom),
            availableFromLabel: getAvailableFromLabelText(
                availableFrom ?? null,
                availableFromLabel
            ),
            priceDrop: getPriceDropProps(priceDrop),
            isFavourite: false,
            presentationType
        };
    });
